/**
 * Admin screen — presidente actions submenu.
 *
 * Lives as a second-level menu accessible only to role === 'admin'.
 * Each entry navigates to an existing screen with admin actions. We
 * only list entries that have a working backend — placeholder routes
 * were removed per the audit so the presidente never sees a "broken"
 * menu item.
 */
import { useEffect, useRef } from 'react';
import { useApp } from '@/state/store';
import { sfx } from '@/lib/audio';
import { WrenchSprite, MegaphoneSprite, CoinSprite, GearSprite } from '@/components/Sprites';

interface AdminAction {
  id: string;
  label: string;
  hint: string;
  Icon: typeof WrenchSprite;
}

const ACTIONS: AdminAction[] = [
  { id: 'incidents', label: 'APROBAR REPORTES', hint: 'A → revisar', Icon: WrenchSprite },
  { id: 'announcements', label: 'CREAR ANUNCIO', hint: 'A → publicar', Icon: MegaphoneSprite },
  { id: 'fees', label: 'CUOTAS MENSUALES', hint: 'A → ver', Icon: CoinSprite },
  { id: 'settings', label: 'CONFIGURACIÓN', hint: 'A → ajustar', Icon: GearSprite },
];

export function AdminScreen() {
  const { goto, menuIndex, setMenuIndex } = useApp();
  const handlersRef = useRef({ menuIndex, ACTIONS: ACTIONS.length, setMenuIndex, goto });
  handlersRef.current = { menuIndex, ACTIONS: ACTIONS.length, setMenuIndex, goto };

  const move = (dir: 'up' | 'down') => {
    const h = handlersRef.current;
    let next = h.menuIndex;
    if (dir === 'up' && next > 0) next -= 1;
    if (dir === 'down' && next < h.ACTIONS - 1) next += 1;
    if (next !== h.menuIndex) {
      sfx.tick();
      h.setMenuIndex(next);
    }
  };

  const activate = () => {
    const h = handlersRef.current;
    const a = ACTIONS[h.menuIndex];
    if (!a) return;
    sfx.confirm();
    h.goto(a.id as any);
  };

  useEffect(() => {
    const onUp = () => handlersRef.current.move ? handlersRef.current.move('up') : handlersRef.current.setMenuIndex(Math.max(0, handlersRef.current.menuIndex - 1));
    const onDown = () => handlersRef.current.move ? handlersRef.current.move('down') : handlersRef.current.setMenuIndex(Math.min(handlersRef.current.ACTIONS - 1, handlersRef.current.menuIndex + 1));
    const onA = () => handlersRef.current.activate();
    const onB = () => handlersRef.current.goto('home');
    // Expose move on the handlersRef so the inline handlers above can call it.
    (handlersRef.current as any).move = move;
    (handlersRef.current as any).activate = activate;
    window.addEventListener('vicin-gb:up', onUp);
    window.addEventListener('vicin-gb:down', onDown);
    window.addEventListener('vicin-gb:a', onA);
    window.addEventListener('vicin-gb:b', onB);
    return () => {
      window.removeEventListener('vicin-gb:up', onUp);
      window.removeEventListener('vicin-gb:down', onDown);
      window.removeEventListener('vicin-gb:a', onA);
      window.removeEventListener('vicin-gb:b', onB);
    };
  }, []);

  return (
    <div className="gb-screen">
      <div className="gb-status">
        <div className="gb-status-left">
          <WrenchSprite size={11} />
          <span className="gb-pix gb-pix-xs">PRESIDENTE</span>
        </div>
      </div>

      <div className="gb-body" style={{ padding: '6px 6px', gap: 4 }}>
        <div
          className="gb-pix gb-mid-text"
          style={{ fontSize: 5, letterSpacing: 0.5, padding: '0 2px', marginBottom: 2 }}
        >
          ACCIONES DE ADMINISTRADOR
        </div>

        <div className="gb-stack" style={{ gap: 3, overflowY: 'auto' }}>
          {ACTIONS.map((a, i) => {
            const selected = i === menuIndex;
            return (
              <div
                key={a.id}
                className="gb-row"
                data-selected={selected}
                data-testid={`admin-action-${a.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 6px',
                  border: '1px solid var(--gb-mid)',
                  background: selected ? 'var(--gb-dark)' : 'var(--gb-bg)',
                  color: selected ? 'var(--gb-bg)' : 'var(--gb-dark)',
                  fontSize: 7,
                  letterSpacing: 0.5,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setMenuIndex(i);
                  setTimeout(() => activate(), 0);
                }}
              >
                <span style={{ width: 8, fontSize: 6, opacity: selected ? 1 : 0.6 }}>
                  {selected ? '▶' : ' '}
                </span>
                <a.Icon size={11} />
                <span style={{ flex: 1 }}>{a.label}</span>
              </div>
            );
          })}
        </div>

        <div
          className="gb-pix gb-pix-xs gb-mid-text"
          style={{ textAlign: 'center', fontSize: 5, marginTop: 4 }}
        >
          [▲▼] NAVEGAR · [A] ENTRAR · [B] VOLVER
        </div>
      </div>
    </div>
  );
}
